import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pages, Packages as PackagesApi } from "@/lib/api";
import EditableHTML from "@/components/EditableHTML";
import Footer from "@/components/Footer";
import ReflectedHeading from "@/components/ReflectedHeading";
import VideoModal from "@/components/VideoModal";
import { ArrowUpRight } from "lucide-react";
import SEO, { SITE_URL } from "@/components/SEO";

const Packages = () => {
  const [content, setContent] = useState({});
  const [packages, setPackages] = useState([]);
  const [home, setHome] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const page = await Pages.get("packages");
        setContent(page.sections || {});
      } catch {}
      try {
        const homePage = await Pages.get("home");
        setHome(homePage.sections || {});
      } catch {}
      try {
        const list = await PackagesApi.list(true);
        setPackages(list);
      } catch {}
    })();
  }, []);

  return (
    <main className="min-h-screen text-slate-100 pt-32">
      <SEO
        title="Thermification™ Equipment Packages"
        description="Professional Thermification™ equipment packages for mold, water, and disaster-response drying — Core, Elite, Plus, Ultra, Pro, and Master configurations. Indirect-fired dry heat, high-volume airflow, chemical-free operation."
        path="/equipment"
        breadcrumbs={[{ name: "Equipment", path: "/equipment" }]}
        schema={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Thermification™ Equipment Packages",
          itemListElement: packages.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/equipment/${p.slug}`,
            name: p.name,
          })),
        }}
      />
      {content.intro_video_url && (
        <VideoModal
          url={content.intro_video_url}
          title="Equipment packages — intro video"
        />
      )}
      <section className="water-section max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="packages-page">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-5">Equipment store</div>
          <ReflectedHeading
            as="h1"
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight chrome-gradient-text"
          >
            <EditableHTML html={content.page_title} as="span" />
          </ReflectedHeading>
          <EditableHTML
            html={content.page_subtitle}
            className="text-slate-400 mt-8 text-base sm:text-lg max-w-2xl"
          />
          {content.intro_video_caption && (
            <EditableHTML
              html={content.intro_video_caption}
              className="mt-6 inline-flex items-center gap-2 text-sm sm:text-base text-atc-blue border-l-2 border-atc-blue/60 pl-4 italic max-w-2xl"
              testId="packages-intro-video-caption"
            />
          )}
        </div>

        {content.equipment_intro_html && (
          <div className="mt-16 max-w-5xl mx-auto">
            <EditableHTML
              html={content.equipment_intro_html}
              className="prose-styled rich-content text-slate-200"
            />
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
          {packages.map((p) => (
            <Link
              key={p.id}
              to={`/equipment/${p.slug}`}
              className="glass glass-hover rounded-2xl overflow-hidden group"
              data-testid={`package-card-${p.slug}`}
            >
              <div
                className="aspect-[4/3] bg-contain bg-center bg-no-repeat bg-slate-950/60 border-b border-white/5"
                style={{ backgroundImage: `url(${p.image_url})` }}
              />
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-display font-bold text-lg text-white">{p.name}</h3>
                  <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-atc-blue transition" />
                </div>
                <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">
                  {p.short_description}
                </p>
                <div className="mt-5 inline-flex items-center text-xs uppercase tracking-[0.18em] text-atc-green">
                  {p.price || "Request a Quote"}
                </div>
              </div>
            </Link>
          ))}
        </div>
        {packages.length === 0 && (
          <div className="mt-16 text-slate-400 text-sm">No equipment listed yet.</div>
        )}
      </section>
      <Footer content={home} />
    </main>
  );
};

export default Packages;
